import { useCallback, useEffect, useMemo, useState } from "react";
import { MiScheduler } from "@miplatform/components";
import {
  ENTITY_TYPES,
  MILLISECONDS_PER_SECOND,
  SNAP_INTERVAL_IN_HOURS,
  VIEWS,
} from "@miplatform/constants";
import { Box } from "@mui/material";
import { EmptyUser } from "../Schedule/Scheduler/EmptyUser/EmptyUser";
import { TaskCard } from "../Schedule/Scheduler/TaskCard/TaskCard";
import { UserDetails } from "../Schedule/Scheduler/UserDetails/UserDetails";
import { ViewConfigProvider } from "../Schedule/Scheduler/ViewConfigContext";
import { useSchedulerController } from "../Schedule/Scheduler/useSchedulerController";
import { CalendarToolBar } from "./CalendarToolBar/CalendarToolBar";
import { useCalendarFilterGroup } from "./CalendarToolBar/useCalendarFilterGroup";

export const SingleDayScheduler = ({ calendar, date, calendarSet }) => {
  const {
    config,
    time,
    selectedUserId,
    attemptsLoading,
    displayData,
    buckets,
    data,
    stopAndRemoveHub,
    startConnectionAndSubscribe,
    loadSchedulerConfig,
    handleOpenCloseoutDrawer,
    onCardHover,
    setSelectedUserId,
    onAttemptUpdated,
    onEventUpdate,
    onEventCreate,
    onColumnHeaderClick,
    onDrag,
    loadBuckets,
    processData,
    users,
    updateTime,
  } = useSchedulerController({ viewConfig: calendar, calendarSet });

  const [zoom, setZoom] = useState(1);

  const {
    showFilters: showCalendarFilters,
    toggleFilters: toggleCalendarFilters,
    filters,
    setFilters,
    appliedFilters,
    setAppliedFilters,
    builderOptions,
    communityOptions,
    statusOptions,
    handleFilterChange,
  } = useCalendarFilterGroup();

  const appliedFilterLabels = useMemo(
    () =>
      [
        appliedFilters.status?.label,
        appliedFilters.builder?.label,
        appliedFilters.community?.label,
      ].filter(Boolean),
    [appliedFilters]
  );

  const hasPendingFilters = useMemo(() => {
    return (
      !!filters.status ||
      !!filters.builder ||
      !!filters.community ||
      !!filters.dueDate ||
      appliedFilterLabels.length > 0
    );
  }, [filters, appliedFilterLabels]);

  const handleApplyCalendarFilters = useCallback(() => {
    setAppliedFilters(filters);
    toggleCalendarFilters();
  }, [filters, setAppliedFilters, toggleCalendarFilters]);

  const handleResetCalendarFilters = useCallback(() => {
    const emptyFilters = {
      status: null,
      builder: null,
      community: null,
      dueDate: null,
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  }, [setFilters, setAppliedFilters]);

  useEffect(() => {
    stopAndRemoveHub();
    if (calendar?.trays?.length || calendar.technicians?.length) {
      startConnectionAndSubscribe();
    }
  }, [calendar]);

  const snappedTime = Math.floor(time.hour / SNAP_INTERVAL_IN_HOURS);

  useEffect(() => {
    loadSchedulerConfig();
  }, [calendar?.modifiedOn, calendar?.displayBoards]);

  useEffect(() => {
    if (calendar?.trays?.length) {
      loadBuckets(calendar);
    }
  }, [calendar]);

  useEffect(() => {
    processData();
  }, [snappedTime, data, users]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      updateTime();
    }, MILLISECONDS_PER_SECOND);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const TaskCardPlus = useCallback(
    (props) => {
      return (
        <TaskCard
          isNHC
          {...props}
          onAttemptUpdated={onAttemptUpdated}
          handleOpenCloseoutDrawer={handleOpenCloseoutDrawer}
          calendarSet={calendarSet}
        />
      );
    },
    [config]
  );

  return (
    <ViewConfigProvider viewConfig={config} calendarSet={calendarSet}>
      <Box display="flex" overflow="hidden" flex={1} flexDirection="row">
        <Box
          display="flex"
          overflow="hidden"
          flex={1}
          flexDirection="column"
          padding={2}
          gap={1}
        >
          <CalendarToolBar
            zoom={zoom}
            setZoom={setZoom}
            showWorkOrderFilters={
              calendarSet?.eventType === ENTITY_TYPES.WORK_ORDER
            }
            showCalendarFilters={showCalendarFilters}
            toggleCalendarFilters={toggleCalendarFilters}
            filters={filters}
            setFilters={setFilters}
            appliedFilters={appliedFilters}
            builderOptions={builderOptions}
            communityOptions={communityOptions}
            statusOptions={statusOptions}
            hasPendingFilters={hasPendingFilters}
            onApplyFilters={handleApplyCalendarFilters}
            onResetFilters={handleResetCalendarFilters}
            handleFilterChange={handleFilterChange}
          />
          <MiScheduler
            config={config}
            data={displayData}
            buckets={buckets[calendar.id] || []}
            users={users}
            EmptyUser={EmptyUser}
            view={VIEWS.DAY}
            date={date}
            time={time}
            selectedUserId={selectedUserId}
            setSelectedUserId={setSelectedUserId}
            attemptsLoading={attemptsLoading}
            ColumnHeader={UserDetails}
            EventCard={TaskCardPlus}
            onColumnHeaderClick={onColumnHeaderClick}
            onEventCreate={onEventCreate}
            onEventUpdate={onEventUpdate}
            onDrag={onDrag}
            onCardHover={onCardHover}
            zoom={zoom}
          />
        </Box>
      </Box>
    </ViewConfigProvider>
  );
};
